
if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();

        this.dispatchEvent(
          new CustomEvent('product-info:loaded', {
            bubbles: true,
          })
        );
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');

        if (!this.quantityForm) return;

        this.setQuantityBoundries();

        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html
            .querySelectorAll('.scroll-trigger')
            .forEach((element) =>
              element.classList.add('scroll-trigger--cancel')
            )
        );

        this.postProcessHtmlCallbacks.push(() => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({
        data: { event, target, selectedOptionValues },
      }) {
        if (!this.contains(event.target)) return;

        /*
         * Temporarily disable Add to Cart while Shopify
         * loads the newly selected variant.
         */
        this.resetProductFormState();

        const productUrl =
          target.dataset.productUrl ||
          this.pendingRequestUrl ||
          this.dataset.url;

        this.pendingRequestUrl = productUrl;

        const shouldSwapProduct = this.dataset.url !== productUrl;

        const shouldFetchFullPage =
          this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(
            productUrl,
            selectedOptionValues,
            shouldFetchFullPage
          ),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;

        if (!productForm) return;

        productForm.toggleSubmitButton(true);

        productForm.handleErrorMessage?.();
      }

      /*
       * ---------------------------------------------------------
       * IMPORTANT FIX
       * ---------------------------------------------------------
       *
       * Synchronizes the Add to Cart button with the button
       * returned by Shopify after variant selection.
       *
       * This prevents the Add to Cart button from disappearing
       * or staying in the wrong state until page refresh.
       */
      updateAddToCartButton(html, variant) {
        const productForm = this.productForm;

        if (!productForm) return;

        const currentButton = productForm.querySelector(
          `#ProductSubmitButton-${this.sectionId}`
        );

        const updatedButton = html.getElementById(
          `ProductSubmitButton-${this.sectionId}`
        );

        /*
         * If Shopify did not return the button, fall back
         * to the variant availability.
         */
        if (!updatedButton) {
          if (variant) {
            const available =
              variant.available !== false;

            productForm.toggleSubmitButton(
              !available,
              available
                ? window.variantStrings?.addToCart || 'Add to cart'
                : window.variantStrings?.soldOut || 'Sold out'
            );
          } else {
            productForm.toggleSubmitButton(
              true,
              window.variantStrings?.unavailable || 'Unavailable'
            );
          }

          return;
        }

        /*
         * Copy the disabled state from Shopify's newly rendered
         * button.
         */
        const isDisabled = updatedButton.hasAttribute('disabled');

        /*
         * If the existing button is still present, update it
         * directly instead of replacing the entire product form.
         *
         * This is important because replacing the whole form can
         * break product-form event listeners.
         */
        if (currentButton) {
          currentButton.disabled = isDisabled;

          if (isDisabled) {
            currentButton.setAttribute('disabled', 'disabled');
          } else {
            currentButton.removeAttribute('disabled');
          }

          /*
           * Keep the button's HTML/text in sync with Shopify.
           * This also handles Sold Out / Add to Cart text.
           */
          if (updatedButton.innerHTML !== currentButton.innerHTML) {
            currentButton.innerHTML = updatedButton.innerHTML;
          }

          /*
           * Copy important accessibility attributes.
           */
          const ariaDisabled =
            updatedButton.getAttribute('aria-disabled');

          if (ariaDisabled !== null) {
            currentButton.setAttribute(
              'aria-disabled',
              ariaDisabled
            );
          } else {
            currentButton.removeAttribute('aria-disabled');
          }

          /*
           * Make sure the button is visible when the selected
           * variant is available.
           */
          if (!isDisabled) {
            currentButton.style.removeProperty('display');
            currentButton.classList.remove('hidden');
          }

          /*
           * Make sure the button is not incorrectly hidden when
           * the variant is available.
           */
          if (variant?.available) {
            currentButton.style.removeProperty('visibility');
            currentButton.style.removeProperty('opacity');
          }

          return;
        }

        /*
         * If the current button does not exist at all,
         * replace the product form's button area with the
         * button returned by Shopify.
         */
        const productFormElement =
          productForm.querySelector('form');

        if (!productFormElement) return;

        const existingSubmitButton = productFormElement.querySelector(
          '[type="submit"]'
        );

        if (existingSubmitButton) {
          existingSubmitButton.replaceWith(
            updatedButton.cloneNode(true)
          );
        } else {
          productFormElement.appendChild(
            updatedButton.cloneNode(true)
          );
        }
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage
            ? "product-info[id^='MainProduct']"
            : 'product-info';

          const variant = this.getSelectedVariant(
            html.querySelector(selector)
          );

          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML =
              html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({
        requestUrl,
        targetId,
        callback,
      }) {
        this.abortController?.abort();

        this.abortController = new AbortController();

        fetch(requestUrl, {
          signal: this.abortController.signal,
        })
          .then((response) => {
            if (!response.ok) {
              throw new Error(
                `HTTP error! Status: ${response.status}`
              );
            }

            return response.text();
          })
          .then((responseText) => {
            this.pendingRequestUrl = null;

            const html = new DOMParser().parseFromString(
              responseText,
              'text/html'
            );

            callback(html);
          })
          .then(() => {
            /*
             * Keep focus on the option the customer selected.
             */
            document
              .querySelector(`#${targetId}`)
              ?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log(
                'Fetch aborted by user'
              );
            } else {
              console.error(
                'Product variant update error:',
                error
              );

              /*
               * If the request fails, re-enable the existing
               * Add to Cart button so it does not remain hidden
               * or disabled forever.
               */
              this.productForm?.toggleSubmitButton(
                false,
                window.variantStrings?.addToCart ||
                  'Add to cart'
              );
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        if (!productInfoNode) return null;

        const selectedVariant =
          productInfoNode.querySelector(
            'variant-selects [data-selected-variant]'
          )?.innerHTML;

        return !!selectedVariant
          ? JSON.parse(selectedVariant)
          : null;
      }

      buildRequestUrlWithParams(
        url,
        optionValues,
        shouldFetchFullPage = false
      ) {
        const params = [];

        if (!shouldFetchFullPage) {
          params.push(
            `section_id=${this.sectionId}`
          );
        }

        if (optionValues.length) {
          params.push(
            `option_values=${optionValues.join(',')}`
          );
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects =
          html.querySelector('variant-selects');

        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(
            this.variantSelectors,
            variantSelects,
            this.preProcessHtmlCallbacks
          );
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant =
            this.getSelectedVariant(html);

          /*
           * Update pickup availability.
           */
          this.pickupAvailability?.update(
            variant
          );

          /*
           * Update variant dropdowns / radio buttons.
           */
          this.updateOptionValues(html);

          /*
           * Update URL with selected variant.
           */
          this.updateURL(
            productUrl,
            variant?.id
          );

          /*
           * Update hidden variant ID input.
           */
          this.updateVariantInputs(
            variant?.id
          );

          /*
           * ---------------------------------------------------
           * IMPORTANT:
           * Synchronize Add to Cart button immediately after
           * updating the variant.
           * ---------------------------------------------------
           */
          this.updateAddToCartButton(
            html,
            variant
          );

          /*
           * No valid variant selected.
           */
          if (!variant) {
            this.setUnavailable();
            return;
          }

          /*
           * Update product media.
           */
          this.updateMedia(
            html,
            variant?.featured_media?.id
          );

          const updateSourceFromDestination = (
            id,
            shouldHide = () => false
          ) => {
            const source =
              html.getElementById(
                `${id}-${this.sectionId}`
              );

            const destination =
              this.querySelector(
                `#${id}-${this.dataset.section}`
              );

            if (source && destination) {
              destination.innerHTML =
                source.innerHTML;

              destination.classList.toggle(
                'hidden',
                shouldHide(source)
              );
            }
          };

          /*
           * Update price.
           */
          updateSourceFromDestination(
            'price'
          );

          /*
           * Update SKU.
           */
          updateSourceFromDestination(
            'Sku',
            ({ classList }) =>
              classList.contains('hidden')
          );

          /*
           * Update inventory.
           */
          updateSourceFromDestination(
            'Inventory',
            ({ innerText }) =>
              innerText === ''
          );

          /*
           * Update volume.
           */
          updateSourceFromDestination(
            'Volume'
          );

          /*
           * Update price per item.
           */
          updateSourceFromDestination(
            'Price-Per-Item',
            ({ classList }) =>
              classList.contains('hidden')
          );

          /*
           * Update quantity rules.
           */
          this.updateQuantityRules(
            this.sectionId,
            html
          );

          this.querySelector(
            `#Quantity-Rules-${this.dataset.section}`
          )?.classList.remove('hidden');

          this.querySelector(
            `#Volume-Note-${this.dataset.section}`
          )?.classList.remove('hidden');

          /*
           * Final Add to Cart state.
           *
           * This is intentionally done AGAIN after all
           * product information has been updated. It makes
           * sure another theme script does not leave the button
           * disabled after variant selection.
           */
          const submitButton =
            html.getElementById(
              `ProductSubmitButton-${this.sectionId}`
            );

          const submitButtonDisabled =
            submitButton?.hasAttribute(
              'disabled'
            ) ?? !variant.available;

          const currentProductForm =
            this.productForm;

          if (currentProductForm) {
            currentProductForm.toggleSubmitButton(
              submitButtonDisabled,
              variant.available
                ? window.variantStrings?.addToCart ||
                    'Add to cart'
                : window.variantStrings?.soldOut ||
                    'Sold out'
            );

            /*
             * Force the button to be visible when the variant
             * is available.
             */
            if (
              variant.available &&
              !submitButtonDisabled
            ) {
              const button =
                currentProductForm.querySelector(
                  `#ProductSubmitButton-${this.sectionId}`
                );

              if (button) {
                button.disabled = false;
                button.removeAttribute(
                  'disabled'
                );
                button.classList.remove(
                  'hidden'
                );

                button.style.removeProperty(
                  'display'
                );

                button.style.removeProperty(
                  'visibility'
                );

                button.style.removeProperty(
                  'opacity'
                );
              }
            }
          }

          /*
           * Publish Shopify's variant change event.
           */
          publish(
            PUB_SUB_EVENTS.variantChange,
            {
              data: {
                sectionId:
                  this.sectionId,
                html,
                variant,
              },
            }
          );
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input =
            productForm.querySelector(
              'input[name="id"]'
            );

          if (!input) return;

          input.value =
            variantId ?? '';

          input.dispatchEvent(
            new Event('change', {
              bubbles: true,
            })
          );
        });
      }

      updateURL(url, variantId) {
        this.querySelector(
          'share-button'
        )?.updateUrl(
          `${window.shopUrl}${url}${
            variantId
              ? `?variant=${variantId}`
              : ''
          }`
        );

        if (
          this.dataset.updateUrl ===
          'false'
        ) {
          return;
        }

        window.history.replaceState(
          {},
          '',
          `${url}${
            variantId
              ? `?variant=${variantId}`
              : ''
          }`
        );
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(
          true,
          window.variantStrings?.unavailable ||
            'Unavailable'
        );

        const selectors = [
          'price',
          'Inventory',
          'Sku',
          'Price-Per-Item',
          'Volume-Note',
          'Volume',
          'Quantity-Rules',
        ]
          .map(
            (id) =>
              `#${id}-${this.dataset.section}`
          )
          .join(', ');

        document
          .querySelectorAll(selectors)
          .forEach(({ classList }) =>
            classList.add('hidden')
          );
      }

      updateMedia(
        html,
        variantFeaturedMediaId
      ) {
        if (!variantFeaturedMediaId) return;

        const mediaGallery =
          this.querySelector(
            'media-gallery'
          );

        if (
          mediaGallery &&
          typeof mediaGallery.setActiveMedia ===
            'function'
        ) {
          mediaGallery.setActiveMedia(
            `${this.dataset.section}-${variantFeaturedMediaId}`,
            true
          );
        }

        const modalContent =
          this.productModal?.querySelector(
            '.product-media-modal__content'
          );

        const newModalContent =
          html.querySelector(
            'product-modal .product-media-modal__content'
          );

        if (
          modalContent &&
          newModalContent
        ) {
          modalContent.innerHTML =
            newModalContent.innerHTML;
        }
      }

      setQuantityBoundries() {
        if (!this.quantityInput) return;

        const data = {
          cartQuantity:
            this.quantityInput.dataset
              .cartQuantity
              ? parseInt(
                  this.quantityInput.dataset
                    .cartQuantity
                )
              : 0,

          min:
            this.quantityInput.dataset.min
              ? parseInt(
                  this.quantityInput.dataset.min
                )
              : 1,

          max:
            this.quantityInput.dataset.max
              ? parseInt(
                  this.quantityInput.dataset.max
                )
              : null,

          step: this.quantityInput.step
            ? parseInt(
                this.quantityInput.step
              )
            : 1,
        };

        let min = data.min;

        const max =
          data.max === null
            ? data.max
            : data.max -
              data.cartQuantity;

        if (max !== null) {
          min = Math.min(
            min,
            max
          );
        }

        if (
          data.cartQuantity >=
          data.min
        ) {
          min = Math.min(
            min,
            data.step
          );
        }

        this.quantityInput.min =
          min;

        if (max) {
          this.quantityInput.max =
            max;
        } else {
          this.quantityInput.removeAttribute(
            'max'
          );
        }

        this.quantityInput.value =
          min;

        publish(
          PUB_SUB_EVENTS.quantityUpdate,
          undefined
        );
      }

      fetchQuantityRules() {
        const currentVariantId =
          this.productForm
            ?.variantIdInput
            ?.value;

        if (!currentVariantId) return;

        const loadingSpinner =
          this.querySelector(
            '.quantity__rules-cart .loading__spinner'
          );

        loadingSpinner?.classList.remove(
          'hidden'
        );

        return fetch(
          `${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`
        )
          .then((response) =>
            response.text()
          )
          .then((responseText) => {
            const html =
              new DOMParser().parseFromString(
                responseText,
                'text/html'
              );

            this.updateQuantityRules(
              this.dataset.section,
              html
            );
          })
          .catch((e) =>
            console.error(e)
          )
          .finally(() =>
            loadingSpinner?.classList.add(
              'hidden'
            )
          );
      }

      updateQuantityRules(
        sectionId,
        html
      ) {
        if (!this.quantityInput) return;

        this.setQuantityBoundries();

        const quantityFormUpdated =
          html.getElementById(
            `Quantity-Form-${sectionId}`
          );

        if (!quantityFormUpdated) return;

        const selectors = [
          '.quantity__input',
          '.quantity__rules',
          '.quantity__label',
        ];

        for (
          let selector of selectors
        ) {
          const current =
            this.quantityForm?.querySelector(
              selector
            );

          const updated =
            quantityFormUpdated.querySelector(
              selector
            );

          if (
            !current ||
            !updated
          ) {
            continue;
          }

          if (
            selector ===
            '.quantity__input'
          ) {
            const attributes = [
              'data-cart-quantity',
              'data-min',
              'data-max',
              'step',
            ];

            for (
              let attribute of attributes
            ) {
              const valueUpdated =
                updated.getAttribute(
                  attribute
                );

              if (
                valueUpdated !==
                null
              ) {
                current.setAttribute(
                  attribute,
                  valueUpdated
                );
              } else {
                current.removeAttribute(
                  attribute
                );
              }
            }
          } else {
            current.innerHTML =
              updated.innerHTML;

            if (
              selector ===
              '.quantity__label'
            ) {
              const updatedAriaLabelledBy =
                updated.getAttribute(
                  'aria-labelledby'
                );

              if (
                updatedAriaLabelledBy
              ) {
                current.setAttribute(
                  'aria-labelledby',
                  updatedAriaLabelledBy
                );

                const labelId =
                  updatedAriaLabelledBy;

                const currentHiddenLabel =
                  document.getElementById(
                    labelId
                  );

                const updatedHiddenLabel =
                  html.getElementById(
                    labelId
                  );

                if (
                  currentHiddenLabel &&
                  updatedHiddenLabel
                ) {
                  currentHiddenLabel.textContent =
                    updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(
          `product-form`
        );
      }

      get productModal() {
        return document.querySelector(
          `#ProductModal-${this.dataset.section}`
        );
      }

      get pickupAvailability() {
        return this.querySelector(
          `pickup-availability`
        );
      }

      get variantSelectors() {
        return this.querySelector(
          'variant-selects'
        );
      }

      get relatedProducts() {
        const relatedProductsSectionId =
          SectionId.getIdForSection(
            SectionId.parseId(
              this.sectionId
            ),
            'related-products'
          );

        return document.querySelector(
          `product-recommendations[data-section-id^="${relatedProductsSectionId}"]`
        );
      }

      get quickOrderList() {
        const quickOrderListSectionId =
          SectionId.getIdForSection(
            SectionId.parseId(
              this.sectionId
            ),
            'quick_order_list'
          );

        return document.querySelector(
          `quick-order-list[data-id^="${quickOrderListSectionId}"]`
        );
      }

      get sectionId() {
        return (
          this.dataset.originalSection ||
          this.dataset.section
        );
      }
    }
  );
}

